import { JsonViewer } from '@/components/JsonViewer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { homeDocumentState, homeHqlCatalogState, hqlCatalogState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { type HomeDocument, type HqlCatalog, serializeHomeDocument } from '@rock/hql';
import type { SpaceId } from '@rock/types';
import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { DryRunCard } from './DryRunCard';
import { QueryBoxCard } from './QueryBoxCard';

/**
 * What applies-to queries see of this home, and what they'd do here. Reads only: nothing on this tab
 * writes to the home or to any consideration.
 */
export const HomeDocumentTab: FC = () => {
    const [doc, docLoadable] = useLoadableAtomValue(homeDocumentState);
    const [homeCatalog] = useLoadableAtomValue(homeHqlCatalogState);
    const [catalog] = useLoadableAtomValue(hqlCatalogState);

    if (!doc || !homeCatalog || !catalog) {
        return (
            <Card>
                <CardContent className="p-6 text-muted-foreground">
                    {docLoadable.state === 'hasError' ? 'Could not build the Home Document.' : 'Loading…'}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <QueryPlayground doc={doc} homeCatalog={homeCatalog} catalog={catalog} />
            <HomeDocumentCard doc={doc} />
        </div>
    );
};

/**
 * The query box and the dry run, which share the query being tried. The query's state lives here
 * rather than in the tab so typing never re-renders the JSON viewer: its internal effects re-dispatch
 * on every render, and at typing speed that runs past React's nested-update limit.
 */
const QueryPlayground: FC<{ doc: HomeDocument; homeCatalog: HqlCatalog; catalog: HqlCatalog }> = ({
    doc,
    homeCatalog,
    catalog,
}) => {
    // A query picked from the dry run keeps its consideration's space until the text is edited.
    const [tried, setTried] = useState<{ query: string; spaceId?: SpaceId }>({ query: '' });
    const queryBoxRef = useRef<HTMLDivElement>(null);

    function tryQuery(query: string, spaceId: SpaceId | undefined) {
        setTried({ query, spaceId });
        queryBoxRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    return (
        <>
            <div ref={queryBoxRef}>
                <QueryBoxCard
                    doc={doc}
                    catalog={homeCatalog}
                    query={tried.query}
                    spaceId={tried.spaceId}
                    onQueryChange={(query) => setTried({ query })}
                />
            </div>
            <DryRunCard doc={doc} catalog={catalog} onTryQuery={tryQuery} />
        </>
    );
};

const HomeDocumentCard: FC<{ doc: HomeDocument }> = ({ doc }) => {
    const serialized = useMemo(() => JSON.parse(serializeHomeDocument(doc)), [doc]);
    return (
        <Card>
            <CardHeader>
                <CardTitle>Home Document</CardTitle>
                <CardDescription>
                    Active equipment and info answers, normalized. A missing value is unknown to every query.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <JsonViewer data={serialized} />
            </CardContent>
        </Card>
    );
};
